import { createPortal } from "react-dom";
import { useModalFocus } from "../services/useModalFocus";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Pause, Play, Square } from "lucide-react";
import { MockAPI } from "../services/api";
import { workState, discardWorkCommands } from "../services/contributionApi";
import ContributionReview, { WorkCorrection } from "./ContributionReview";
import { Project, WorkLog } from "../types";
import { Button } from "./ui";
import { useAuth } from "../services/authContext";

interface LogWorkSheetProps {
  onClose: () => void;
}

const ACTIVE_PROJECT_STATUSES = new Set(["todo", "in_process", "paused"]);

const formatDuration = (durationMs: number) => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
};

const LogWorkSheet: React.FC<LogWorkSheetProps> = ({ onClose }) => {
  const { user } = useAuth();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, true, onClose);
  const userId = user?.email || "anonymous@local";
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [workLog, setWorkLog] = useState<WorkLog | null>(null);
  const [history, setHistory] = useState<WorkLog[]>([]);
  const [busy, setBusy] = useState(false);
  const [ticker, setTicker] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);

  const loadInitialState = async () => {
    setError(null);
    try {
      const data = await workState();
      const allProjects = data.projects,
        latestWorkLogs = data.work.filter(
          (w) => w.contributor_email === userId || w.user_id === userId,
        ),
        activeWorkLog = latestWorkLogs.find((w) => w.state === "active");
      const eligibleProjects = allProjects.filter((project) =>
        ACTIVE_PROJECT_STATUSES.has(project.status),
      );
      setProjects(eligibleProjects);
      setHistory(latestWorkLogs);

      const fallbackLog = latestWorkLogs
        .filter((item) => item.state !== "stopped")
        .sort(
          (a, b) =>
            Date.parse(b.updated_at || b.start_time || '') -
            Date.parse(a.updated_at || a.start_time || ''),
        )[0];

      const restoredLog = activeWorkLog || fallbackLog || null;
      setWorkLog(restoredLog);
      setSelectedProjectId(
        restoredLog?.project_id || eligibleProjects[0]?.id || "",
      );
    } catch (err: any) {
      if ([401, 403].includes(err?.status)) {
        setProjects([]);
        setHistory([]);
        setWorkLog(null);
      }
      setError(err?.message || "Failed to load timer state.");
    }
  };

  useEffect(() => {
    setProjects([]);
    setHistory([]);
    setWorkLog(null);
    void loadInitialState();
    const reconnect = () => void loadInitialState();
    window.addEventListener("online", reconnect);
    return () => window.removeEventListener("online", reconnect);
  }, [userId]);

  useEffect(() => {
    if (!workLog || workLog.state !== "active") return;
    const interval = window.setInterval(() => setTicker(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [workLog]);

  const selectedProject = useMemo(
    () =>
      projects.find(
        (project) => project.id === (workLog?.project_id || selectedProjectId),
      ),
    [projects, selectedProjectId, workLog],
  );
  const elapsed = MockAPI.computeWorkLogDuration(workLog || undefined, ticker);

  const handleStart = async () => {
    if (!selectedProjectId) {
      setError("Select a project first.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const started = await MockAPI.startTimerForProject({
        project_id: selectedProjectId,
        user_id: userId,
      });
      setWorkLog(started);
    } catch (err: any) {
      setError(err?.message || "Unable to start timer.");
    } finally {
      setBusy(false);
    }
  };

  const handlePause = async () => {
    if (!workLog) return;
    setError(null);
    setBusy(true);
    try {
      const paused = await MockAPI.pauseTimer(workLog.id);
      setWorkLog(paused);
    } catch (err: any) {
      setError(err?.message || "Unable to pause timer.");
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async () => {
    if (!workLog) return;
    setError(null);
    setBusy(true);
    try {
      const resumed = await MockAPI.resumeWorkTimer(workLog.id);
      setWorkLog(resumed);
    } catch (err: any) {
      setError(err?.message || "Unable to resume timer.");
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    if (!workLog) return;
    setError(null);
    setBusy(true);
    try {
      const stopped = await MockAPI.stopTimer(workLog.id);
      setWorkLog(stopped);
      await loadInitialState();
    } catch (err: any) {
      setError(err?.message || "Unable to stop timer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] bg-black/40 flex items-end sm:items-center sm:justify-center">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Log work"
        className="w-full sm:max-w-lg max-h-[80vh] bg-white rounded-t-2xl sm:rounded-2xl overflow-y-auto shadow-2xl transform transition-all duration-200 translate-y-0 opacity-100"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 flex justify-between items-center px-4 py-3">
          <h2 className="font-semibold text-gray-900">Log Work</h2>
          <button
            aria-label="Close work logging"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div role="alert" className="text-sm text-red-600">
              <p>{error}</p>
              {error.includes("reconciliation") && (
                <button
                  className="underline mt-2"
                  onClick={() =>
                    void (async () => {
                      if (
                        window.confirm(
                          "Discard unsent timer changes and reload confirmed work?",
                        )
                      ) {
                        await discardWorkCommands();
                        await loadInitialState();
                      }
                    })()
                  }
                >
                  Discard unsent timer changes
                </button>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Project
            </label>
            <select
              aria-label="Project"
              className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm p-2"
              value={workLog?.project_id || selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={!!workLog && workLog.state !== "stopped"}
            >
              <option value="">-- Select project --</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Current session</p>
            <p className="text-lg font-semibold text-gray-900">
              {selectedProject?.name || "No project selected"}
            </p>
            <p className="mt-2 text-3xl font-mono tracking-wide text-usda-700">
              {formatDuration(elapsed)}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              State: {workLog?.state || "idle"}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {!workLog || workLog.state === "stopped" ? (
              <Button
                onClick={handleStart}
                disabled={busy || !selectedProjectId}
              >
                <Play className="h-4 w-4 mr-2" />
                Start
              </Button>
            ) : null}
            {workLog?.state === "active" ? (
              <Button variant="outline" disabled={busy} onClick={handlePause}>
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </Button>
            ) : null}
            {workLog?.state === "paused" ? (
              <Button variant="outline" disabled={busy} onClick={handleResume}>
                <Play className="h-4 w-4 mr-2" />
                Resume
              </Button>
            ) : null}
            {workLog && workLog.state !== "stopped" ? (
              <Button variant="danger" disabled={busy} onClick={handleStop}>
                <Square className="h-4 w-4 mr-2" />
                Stop
              </Button>
            ) : null}
          </div>
          <section className="space-y-3 mt-5">
            <h3 className="font-semibold">Your recent work</h3>
            {history
              .filter((w) => w.state === "stopped")
              .slice(0, 8)
              .map((w) => (
                <article key={w.id} className="border rounded-xl p-3 text-sm">
                  <p>
                    {projects.find((p) => p.id === w.project_id)?.name ||
                      "Project work"}{" "}
                    · {formatDuration(MockAPI.computeWorkLogDuration(w))}
                  </p>
                  <p>
                    {w.sync_pending
                      ? "Waiting for connection"
                      : w.review_status === "approved"
                        ? "Approved"
                        : w.review_status === "returned"
                          ? "Needs changes"
                          : w.review_status === "unverified"
                            ? "Awaiting verification"
                            : "Recorded"}
                  </p>
                  {w.review_reason && <p>{w.review_reason}</p>}
                  {w.contributor_email && (
                    <WorkCorrection
                      work={w}
                      onSaved={() => void loadInitialState()}
                    />
                  )}
                </article>
              ))}
          </section>
          <ContributionReview />
        </div>
      </div>
    </div>
  );
};

export default LogWorkSheet;
